import React, { useCallback, useEffect, useState } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarMotorista,
  criarMotorista,
  desativarMotorista,
  listarMotoristas,
} from "../../../services/logistica";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Table,
  TableWrapper,
  Textarea,
} from "../../Orcamentos/styles";


function MotoristaForm({ inicial, onSave, onCancel, unidadeId }) {
  const [dados, setDados] = useState({
    nome: inicial?.nome || "", cpf: inicial?.cpf || "",
    cnh: inicial?.cnh || "", telefone: inicial?.telefone || "",
    email: inicial?.email || "", observacoes: inicial?.observacoes || "",
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Nome *</span>
        <Input value={dados.nome} onChange={(e) => setDados({ ...dados, nome: e.target.value })} maxLength={120} required />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>CPF</span>
        <Input value={dados.cpf} onChange={(e) => setDados({ ...dados, cpf: e.target.value })} maxLength={14} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>CNH</span>
        <Input value={dados.cnh} onChange={(e) => setDados({ ...dados, cnh: e.target.value })} maxLength={15} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Telefone</span>
        <Input value={dados.telefone} onChange={(e) => setDados({ ...dados, telefone: e.target.value })} maxLength={30} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Email</span>
        <Input type="email" value={dados.email} onChange={(e) => setDados({ ...dados, email: e.target.value })} />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Observações</span>
        <Textarea rows={2} value={dados.observacoes} onChange={(e) => setDados({ ...dados, observacoes: e.target.value })} />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
        <AsyncButton
          variant="primary"
          onClick={() => onSave({ ...dados, unidade: unidadeId })}
          successMessage="Motorista salvo"
        >
          Salvar
        </AsyncButton>
      </div>
    </div>
  );
}


export default function MotoristasTab() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(null);
  const [unidadeId, setUnidadeId] = useState("");

  const podeManage = isMaster || hasAllPermissions(["motorista:manage"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = await listarMotoristas(request);
      const list = Array.isArray(p) ? p : p?.results || [];
      setItems(list);
      if (list.length > 0 && !unidadeId) setUnidadeId(list[0].unidade);
    } catch (err) {
      setError(err?.message || "Erro.");
    } finally { setLoading(false); }
  }, [request, unidadeId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (unidadeId) return;
    request("/api/unidades").then((d) => {
      const list = Array.isArray(d) ? d : d?.results || [];
      if (list.length > 0) setUnidadeId(list[0].id);
    }).catch(() => {});
  }, [request, unidadeId]);

  const handleSalvar = async (dados) => {
    if (editando === "novo") await criarMotorista(request, dados);
    else await atualizarMotorista(request, editando.id, dados);
    setEditando(null);
    await carregar();
  };

  const handleExcluir = async (m) => {
    if (!window.confirm(`Desativar motorista "${m.nome}"?`)) return;
    await desativarMotorista(request, m.id);
    await carregar();
  };

  return (
    <>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SectionTitle>{loading ? "Carregando..." : `${items.length} motorista(s)`}</SectionTitle>
          {podeManage && (
            <SecondaryButton onClick={() => setEditando("novo")}>
              <FiPlus size={14} /> Novo
            </SecondaryButton>
          )}
        </div>
        {error && <ErrorMsg>{error}</ErrorMsg>}
        {!loading && items.length === 0 && <EmptyMsg>Nenhum motorista.</EmptyMsg>}
        {items.length > 0 && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>CNH</th>
                  <th>Telefone</th>
                  <th>Status</th>
                  {podeManage && <th>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td><strong>{m.nome}</strong></td>
                    <td>{m.cpf || "—"}</td>
                    <td>{m.cnh || "—"}</td>
                    <td>{m.telefone || "—"}</td>
                    <td>
                      <Pill $tone={m.ativo ? "success" : "neutral"}>
                        {m.ativo ? "Ativo" : "Inativo"}
                      </Pill>
                    </td>
                    {podeManage && (
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <SecondaryButton onClick={() => setEditando(m)} style={{ padding: "4px 8px", fontSize: 12 }}>
                            <FiEdit2 size={12} />
                          </SecondaryButton>
                          {m.ativo && (
                            <SecondaryButton onClick={() => handleExcluir(m)} style={{ padding: "4px 8px", fontSize: 12 }}>
                              <FiTrash2 size={12} />
                            </SecondaryButton>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {editando && (
        <SlideOverPanel
          open
          onClose={() => setEditando(null)}
          title={editando === "novo" ? "Novo motorista" : `Editar ${editando.nome}`}
        >
          <MotoristaForm
            inicial={editando === "novo" ? null : editando}
            onSave={handleSalvar}
            onCancel={() => setEditando(null)}
            unidadeId={unidadeId}
          />
        </SlideOverPanel>
      )}
    </>
  );
}
